import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Lock, Shield, History, Bell, Trash2, Loader2, LogOut, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';

function Settings({ user, setUser, onLogout }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  const navigate = useNavigate();
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Login history state
  const [loginHistory, setLoginHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Notifications state
  const [notifications, setNotifications] = useState({
    order_updates: true,
    promotions: false,
    newsletter: false
  });
  const [savingNotifications, setSavingNotifications] = useState(false);

  // Delete account state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  useEffect(() => {
    if (user) {
      fetchLoginHistory();
      // Load notification preferences if available
      if (user.notification_preferences) {
        setNotifications(user.notification_preferences);
      }
    }
  }, [user]);

  const fetchLoginHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/user/login-history`, {
        headers: getAuthHeaders()
      });
      setLoginHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch login history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error(t('auth.passwordMismatch') || 'Пароли не совпадают');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error(t('auth.passwordTooShort') || 'Пароль должен быть не менее 6 символов');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.put(`${API}/user/password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: getAuthHeaders()
      });
      
      toast.success(t('settings.passwordChanged') || 'Пароль успешно изменён');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('settings.passwordError') || 'Ошибка смены пароля');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    try {
      await axios.put(`${API}/user/notifications`, notifications, {
        headers: getAuthHeaders()
      });
      toast.success(t('settings.notificationsSaved') || 'Настройки уведомлений сохранены');
    } catch (error) {
      toast.error(t('settings.notificationsError') || 'Ошибка сохранения');
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'УДАЛИТЬ') {
      toast.error(t('settings.typeDeleteToConfirm') || 'Введите УДАЛИТЬ для подтверждения');
      return;
    }

    setDeletingAccount(true);
    try {
      await axios.delete(`${API}/user/account`, {
        headers: getAuthHeaders()
      });
      
      toast.success(t('settings.accountDeleted') || 'Аккаунт удалён');
      localStorage.removeItem('token');
      setUser(null);
      navigate(`/${currentLang}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('settings.deleteError') || 'Ошибка удаления');
    } finally {
      setDeletingAccount(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to={`/${currentLang}`} className="flex items-center">
            <img 
              src="/logo.jpg" 
              alt="Ainanan" 
              className="h-10 object-contain"
            />
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Button variant="ghost" size="sm" onClick={onLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              {t('common.logout')}
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 max-w-3xl mx-auto w-full px-4 py-8">
        <Link to={`/${currentLang}`} className="inline-flex items-center text-stone-600 hover:text-stone-800 mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('auth.backToCatalog')}
        </Link>

        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <Link to={`/${currentLang}/profile`}>
            <Button variant="outline">
              {t('profile.title') || 'Профиль'}
            </Button>
          </Link>
          <Link to={`/${currentLang}/settings`}>
            <Button variant="default" className="bg-emerald-700 hover:bg-emerald-800">
              {t('settings.title') || 'Настройки'}
            </Button>
          </Link>
          <Link to={`/${currentLang}/orders`}>
            <Button variant="outline">
              {t('common.myOrders')}
            </Button>
          </Link>
          <Link to={`/${currentLang}/addresses`}>
            <Button variant="outline">
              {t('common.myAddresses')}
            </Button>
          </Link>
          {user?.role === 'B2B' && (
            <Link to={`/${currentLang}/legal-entities`}>
              <Button variant="outline">{t('legalEntities.title') || 'Юрлица'}</Button>
            </Link>
          )}
          {user?.role === 'B2B' && (
            <Link to={`/${currentLang}/b2b/networks`}>
              <Button variant="outline">Мои сети</Button>
            </Link>
          )}
        </div>

        <div className="space-y-6">
          {/* Change Password */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-emerald-700" />
                </div>
                <div>
                  <CardTitle className="text-lg">{t('settings.changePassword') || 'Смена пароля'}</CardTitle>
                  <CardDescription>{t('settings.changePasswordDesc') || 'Обновите пароль для защиты аккаунта'}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <Label htmlFor="current_password">{t('settings.currentPassword') || 'Текущий пароль'}</Label>
                  <div className="relative mt-1">
                    <Input
                      id="current_password"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="new_password">{t('settings.newPassword') || 'Новый пароль'}</Label>
                  <div className="relative mt-1">
                    <Input
                      id="new_password"
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirm_password">{t('settings.confirmNewPassword') || 'Подтвердите новый пароль'}</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={changingPassword}
                  className="bg-emerald-700 hover:bg-emerald-800"
                >
                  {changingPassword ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('settings.saving') || 'Сохранение...'}
                    </>
                  ) : (
                    t('settings.updatePassword') || 'Обновить пароль'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Login History */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <History className="w-5 h-5 text-blue-700" />
                </div>
                <div>
                  <CardTitle className="text-lg">{t('settings.loginHistory') || 'История входов'}</CardTitle>
                  <CardDescription>{t('settings.loginHistoryDesc') || 'Последние входы в ваш аккаунт'}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingHistory ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : loginHistory.length > 0 ? (
                <div className="space-y-3">
                  {loginHistory.slice(0, 5).map((entry, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {entry.device || t('settings.unknownDevice') || 'Неизвестное устройство'}
                        </p>
                        <p className="text-xs text-gray-500">{entry.ip_address || '—'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">
                          {new Date(entry.timestamp).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(entry.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-4">
                  {t('settings.noLoginHistory') || 'История входов пуста'}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Email Notifications */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <Bell className="w-5 h-5 text-purple-700" />
                </div>
                <div>
                  <CardTitle className="text-lg">{t('settings.notifications') || 'Уведомления'}</CardTitle>
                  <CardDescription>{t('settings.notificationsDesc') || 'Настройки email уведомлений'}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{t('settings.orderUpdates') || 'Обновления заказов'}</p>
                    <p className="text-sm text-gray-500">{t('settings.orderUpdatesDesc') || 'Уведомления о статусе ваших заказов'}</p>
                  </div>
                  <Switch
                    checked={notifications.order_updates}
                    onCheckedChange={(checked) => setNotifications({ ...notifications, order_updates: checked })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{t('settings.promotions') || 'Акции и скидки'}</p>
                    <p className="text-sm text-gray-500">{t('settings.promotionsDesc') || 'Информация о специальных предложениях'}</p>
                  </div>
                  <Switch
                    checked={notifications.promotions}
                    onCheckedChange={(checked) => setNotifications({ ...notifications, promotions: checked })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{t('settings.newsletter') || 'Новостная рассылка'}</p>
                    <p className="text-sm text-gray-500">{t('settings.newsletterDesc') || 'Новости компании и новинки'}</p>
                  </div>
                  <Switch
                    checked={notifications.newsletter}
                    onCheckedChange={(checked) => setNotifications({ ...notifications, newsletter: checked })}
                  />
                </div>

                <Button
                  onClick={handleSaveNotifications}
                  disabled={savingNotifications}
                  variant="outline"
                  className="mt-2"
                >
                  {savingNotifications ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('settings.saving') || 'Сохранение...'}
                    </>
                  ) : (
                    t('settings.saveNotifications') || 'Сохранить настройки'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Delete Account */}
          <Card className="border-red-200">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-700" />
                </div>
                <div>
                  <CardTitle className="text-lg text-red-700">{t('settings.deleteAccount') || 'Удаление аккаунта'}</CardTitle>
                  <CardDescription>{t('settings.deleteAccountDesc') || 'Безвозвратное удаление аккаунта и всех данных'}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="destructive">
                    <Trash2 className="w-4 h-4 mr-2" />
                    {t('settings.deleteMyAccount') || 'Удалить мой аккаунт'}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-red-600">
                      <AlertTriangle className="w-5 h-5" />
                      {t('settings.confirmDelete') || 'Подтверждение удаления'}
                    </DialogTitle>
                    <DialogDescription>
                      {t('settings.deleteWarning') || 'Это действие нельзя отменить. Все ваши данные, заказы и адреса будут удалены навсегда.'}
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="py-4">
                    <Label>{t('settings.typeDelete') || 'Введите УДАЛИТЬ для подтверждения'}</Label>
                    <Input
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="УДАЛИТЬ"
                      className="mt-2"
                    />
                  </div>

                  <DialogFooter>
                    <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                      {t('common.cancel') || 'Отмена'}
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleDeleteAccount}
                      disabled={deletingAccount || deleteConfirmText !== 'УДАЛИТЬ'}
                    >
                      {deletingAccount ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('settings.deleting') || 'Удаление...'}
                        </>
                      ) : (
                        t('settings.confirmDeleteBtn') || 'Удалить навсегда'
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t py-4 mt-auto">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} Ainanan. {t('common.allRights')}.
        </div>
      </footer>
    </div>
  );
}

export default Settings;
